import { type PrimaryKeyAI } from '@myplayer/gs1';
import { IconQrCode } from '@myplayer/ui';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useOptionalAuth } from './auth-context.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardIcon,
  CardTitle,
  SegmentedControl,
  ToastNotification,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildQrCardContextMenuItems } from './context-menu-items.js';
import { Gs1PrintTabPanel, StyledQrTabPanel } from './qr-customiser-panels.js';
import {
  QR_X_DIMENSION_DEFAULT_MM,
  generateGs1QrPrintAssets,
  getQrExportMimeType,
  triggerBrowserDownload,
  type GeneratedGs1QrPrintAssets,
  type Gs1SymbolSpecTableId,
  type QrExportFormat,
} from './qr-generator.js';
import {
  DEFAULT_SAFE_QR_STYLE,
  evaluateQrStyleRules,
  generateStyledQrAssets,
  type GeneratedStyledQrAssets,
  type QrStyleConfig,
} from './qr-style-rules.js';
import {
  resolveEffectiveQrStyle,
  saveStyledQrDesign,
  type QrCustomisationScope,
  type SaveBrandQrStyleCallableFn,
} from './qr-style-storage.js';

export {
  getSavedBrandQrStyle,
  getSavedWorkspaceQrStyle,
  normalizeBrandKey,
  resetQrStyleStoreForTests,
  resolveEffectiveQrStyle,
  sanitizeSavedQrStyle,
  saveStyledQrDesign,
  type QrCustomisationScope,
  type SaveBrandQrStyleCallableFn,
  type SaveStyledQrDesignOutcome,
  type SaveStyledQrDesignParams,
} from './qr-style-storage.js';

export type QrCustomiserTab = 'gs1' | 'styled';

function selectAssetContent(
  assets: GeneratedGs1QrPrintAssets,
  format: QrExportFormat,
): string | Uint8Array {
  if (format === 'SVG') return assets.svg;
  if (format === 'PDF') return assets.pdf;
  if (format === 'PNG') return assets.png;
  return assets.eps;
}

export interface QrCustomiserCardProps {
  readonly cardTestId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly host: string;
  readonly ai: PrimaryKeyAI | string;
  readonly primaryKey: string;
  readonly fileSlug: string;
  readonly workspaceId?: string;
  readonly brandName?: string;
  readonly tableId?: Gs1SymbolSpecTableId;
  readonly initialXDimensionMm?: number;
  readonly initialTab?: QrCustomiserTab;
  readonly initialStyle?: QrStyleConfig;
  readonly precomputedGs1Assets?: GeneratedGs1QrPrintAssets | null;
  readonly externalDownloadStatus?: {
    readonly format: QrExportFormat;
    readonly assets: GeneratedGs1QrPrintAssets;
  } | null;
  readonly onDownloadGs1?: (format: QrExportFormat, xDimensionMm?: number) => void;
  readonly onSaveStyledDesign?: (style: QrStyleConfig) => void;
  readonly onUpdateWorkspaceCallable?: SaveBrandQrStyleCallableFn;
  readonly onOpenQrCheck?: () => void;
}

export function QrCustomiserCard({
  cardTestId,
  title,
  subtitle,
  host,
  ai,
  primaryKey,
  fileSlug,
  workspaceId = 'ws_default',
  brandName,
  tableId = 'TABLE_5_46',
  initialXDimensionMm = QR_X_DIMENSION_DEFAULT_MM,
  initialTab = 'gs1',
  initialStyle,
  precomputedGs1Assets,
  externalDownloadStatus,
  onDownloadGs1,
  onSaveStyledDesign,
  onUpdateWorkspaceCallable,
  onOpenQrCheck,
}: QrCustomiserCardProps): ReactElement {
  const auth = useOptionalAuth();
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const [activeTab, setActiveTab] = useState<QrCustomiserTab>(initialTab);
  const [xDimensionMm, setXDimensionMm] = useState<number>(initialXDimensionMm);
  const [style, setStyle] = useState<QrStyleConfig>(
    () =>
      initialStyle ??
      resolveEffectiveQrStyle({
        workspaceId,
        brandName,
        fallbackStyle: DEFAULT_SAFE_QR_STYLE,
      }),
  );
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [localDownloaded, setLocalDownloaded] = useState<{
    readonly format: QrExportFormat;
    readonly url: string;
  } | null>(null);
  const [dismissedExternalDownload, setDismissedExternalDownload] =
    useState<QrCustomiserCardProps['externalDownloadStatus']>(null);

  useEffect(() => {
    if (initialStyle !== undefined) {
      setStyle(initialStyle);
      return;
    }
    setStyle(
      resolveEffectiveQrStyle({
        workspaceId,
        brandName,
        fallbackStyle: DEFAULT_SAFE_QR_STYLE,
      }),
    );
  }, [workspaceId, brandName, initialStyle]);

  const gs1Assets = useMemo<GeneratedGs1QrPrintAssets | null>(() => {
    if (
      precomputedGs1Assets !== undefined &&
      precomputedGs1Assets !== null &&
      Math.abs(precomputedGs1Assets.xDimensionMm - xDimensionMm) < 0.0001
    ) {
      return precomputedGs1Assets;
    }
    if (host.trim().length === 0) return null;
    try {
      return generateGs1QrPrintAssets({ host, ai, primaryKey, tableId, xDimensionMm });
    } catch {
      return null;
    }
  }, [precomputedGs1Assets, host, ai, primaryKey, tableId, xDimensionMm]);

  const styledAssets = useMemo<GeneratedStyledQrAssets | null>(() => {
    if (host.trim().length === 0) return null;
    try {
      return generateStyledQrAssets({ host, ai, primaryKey, tableId, xDimensionMm, style });
    } catch {
      return null;
    }
  }, [host, ai, primaryKey, tableId, xDimensionMm, style]);

  const ruleEvaluation = useMemo(() => {
    const decodePassed =
      styledAssets !== null
        ? styledAssets.decodePassed && styledAssets.decodedUrl === styledAssets.url
        : undefined;
    return evaluateQrStyleRules(style, decodePassed !== undefined ? { decodePassed } : undefined);
  }, [style, styledAssets]);
  const resolvedQrUrl = gs1Assets?.url ?? styledAssets?.url ?? '';

  const updateStyle = (patch: Partial<QrStyleConfig>): void => {
    setSavedNotice(null);
    setStyle((prev) => ({ ...prev, ...patch }));
  };

  const handleDownloadGs1Asset = (format: QrExportFormat): void => {
    if (onDownloadGs1) {
      onDownloadGs1(format, xDimensionMm);
      return;
    }
    if (!gs1Assets) return;
    setLocalDownloaded({ format, url: gs1Assets.url });
    const filename = `gs1-qr-${fileSlug}-${gs1Assets.spec.tableNumber}.${format.toLowerCase()}`;
    triggerBrowserDownload(
      filename,
      selectAssetContent(gs1Assets, format),
      getQrExportMimeType(format),
    );
  };

  const handleDownloadStyledAsset = (format: QrExportFormat): void => {
    if (
      !ruleEvaluation.valid ||
      !styledAssets ||
      !styledAssets.decodePassed ||
      styledAssets.decodedUrl !== styledAssets.url
    ) {
      return;
    }
    setLocalDownloaded({ format, url: styledAssets.url });
    const filename = `styled-qr-${fileSlug}.${format.toLowerCase()}`;
    triggerBrowserDownload(
      filename,
      selectAssetContent(styledAssets, format),
      getQrExportMimeType(format),
    );
  };

  const handleSaveStyled = async (): Promise<void> => {
    if (!ruleEvaluation.valid || !styledAssets) return;
    const target: QrCustomisationScope =
      brandName !== undefined && brandName.trim().length > 0
        ? { scope: 'brand', workspaceId, brandName: brandName.trim() }
        : { scope: 'workspace', workspaceId };
    const outcome = await saveStyledQrDesign({
      host,
      ai,
      primaryKey,
      tableId,
      xDimensionMm,
      style,
      target,
      ...(auth?.session?.callerIdentity !== undefined
        ? { caller: auth.session.callerIdentity }
        : {}),
      ...(auth?.session?.orgId !== undefined ? { orgId: auth.session.orgId } : {}),
      ...(onUpdateWorkspaceCallable !== undefined ? { onUpdateWorkspaceCallable } : {}),
    });
    if (!outcome.ok) return;
    onSaveStyledDesign?.(outcome.savedStyle);
    setSavedNotice(
      target.scope === 'brand'
        ? `Styled QR design saved for brand ${target.brandName}.`
        : 'Styled QR design saved.',
    );
  };

  return (
    <Card
      data-testid={cardTestId}
      onContextMenu={bindContextMenu(() =>
        buildQrCardContextMenuItems({
          resolvedQrUrl,
          activeTab,
          onSwitchTab: setActiveTab,
          onDownload:
            activeTab === 'gs1'
              ? handleDownloadGs1Asset
              : ruleEvaluation.valid
                ? handleDownloadStyledAsset
                : undefined,
          onOpenQrCheck,
        }),
      )}
    >
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <CardIcon>
            <IconQrCode />
          </CardIcon>
          <div className="min-w-0 flex-1">
            <CardTitle>{title}</CardTitle>
            <p className="mt-0.5 text-[12px] text-fg-secondary">{subtitle}</p>
          </div>
        </div>
        <SegmentedControl<QrCustomiserTab>
          role="tablist"
          ariaLabel="QR code version"
          value={activeTab}
          onChange={setActiveTab}
          items={[
            {
              value: 'gs1',
              label: 'GS1 print version',
              testId: 'qr-tab-gs1-print',
            },
            {
              value: 'styled',
              label: 'Styled version',
              testId: 'qr-tab-styled',
            },
          ]}
        />
      </CardHeader>

      <CardContent className="space-y-4 text-[12px]">
        {activeTab === 'gs1' ? (
          <Gs1PrintTabPanel
            cardTestId={cardTestId}
            gs1Assets={gs1Assets}
            resolvedQrUrl={resolvedQrUrl}
            xDimensionMm={xDimensionMm}
            onChangeXDimensionMm={setXDimensionMm}
            onDownload={handleDownloadGs1Asset}
            onOpenQrCheck={onOpenQrCheck}
          />
        ) : (
          <StyledQrTabPanel
            cardTestId={cardTestId}
            style={style}
            styledAssets={styledAssets}
            ruleEvaluation={ruleEvaluation}
            savedNotice={savedNotice}
            xDimensionMm={xDimensionMm}
            onChangeXDimensionMm={setXDimensionMm}
            onUpdateStyle={updateStyle}
            onResetSafeDefaults={() => {
              setSavedNotice(null);
              setStyle(DEFAULT_SAFE_QR_STYLE);
            }}
            onSaveStyled={() => {
              void handleSaveStyled();
            }}
            onDownloadStyled={handleDownloadStyledAsset}
          />
        )}

        {((externalDownloadStatus !== null &&
          externalDownloadStatus !== undefined &&
          externalDownloadStatus !== dismissedExternalDownload) ||
          localDownloaded !== null) && (
          <ToastNotification
            data-testid="qr-download-status"
            onDismiss={() => {
              setLocalDownloaded(null);
              setDismissedExternalDownload(externalDownloadStatus ?? null);
            }}
            message={
              <>
                Downloaded {externalDownloadStatus?.format ?? localDownloaded?.format ?? 'SVG'} QR
                code for{' '}
                <code className="font-mono">
                  {externalDownloadStatus?.assets.url ?? localDownloaded?.url ?? resolvedQrUrl}
                </code>
              </>
            }
          />
        )}
        {contextMenuElement}
      </CardContent>
    </Card>
  );
}
